import {
  type Dispatch,
  type SetStateAction,
  createContext,
  forwardRef,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type Ref,
  type RefObject,
} from "react";
import { createPortal } from "react-dom";
import { GroupedVirtuoso, Virtuoso, type GroupedVirtuosoHandle, type VirtuosoHandle } from "react-virtuoso";
import * as R from "remeda";
import parse from "autosuggest-highlight/parse";
import ClickAwayListener from "../ClickAwayListener";
import match from "autosuggest-highlight/match";
import type { TProduct } from "../../../data";
import cn from "mcn";
import { IoSearch } from "react-icons/io5";
import { FaPlus, FaXmark, FaChevronDown } from "react-icons/fa6";
import { CloseIcon } from "../../icons/CloseIcon";
import { findPrefixJumpIndex } from "./prefixJump";
import { Tooltip, TooltipTrigger } from "@artis/ui";

export type TOption = {
  label: string;
  value: string;
  groupValue: string;
  groupLabel?: string;
  groupKey?: string;
  disabled?: boolean;
  /** Explains why a disabled option cannot be picked; shown on hover anywhere in the row. */
  disabledTooltip?: string;
  metadata?: TProduct | undefined;
  hidden?: boolean;
  shortName?: string;
  longName?: string | null;
  sortSourceType?: string;
  sortSourceName?: string;
  sortPackageName?: string;
  /** Shown after the curve name on selected-value pills, e.g. "0.5% BGS CK: NUEVO". */
  sourceLabel?: string;
};

export type TAutocomplete = {
  options: TOption[];
  listHeight?: number;
  disabled?: boolean;
  loading?: boolean;
  autoComplete?: boolean;
  autoFocus?: boolean;
  singleState: [TOption | undefined, Dispatch<SetStateAction<TOption | undefined>>];
  multipleState: [TOption[], Dispatch<SetStateAction<TOption[]>>];
  multiple: boolean;
  onSelectValue?: <T extends TOption>(value: T) => unknown;
  onSelectValues?: <T extends TOption[]>(value: T) => unknown;
  defaultValue?: TOption | TOption[];
  placeholder?: string;
  disableFilter?: boolean;
  onOpen?: () => void;
  onClear?: () => void;
  value?: string;
  className?: string;
  /** Match Dropdown trigger height (27px) for inline form fields. */
  compact?: boolean;
  /** When false, hide the clear control so a required value cannot be wiped. */
  clearable?: boolean;
  /** When false, hide the per-group "Add All" control. Defaults to true for multi-select. */
  showAddAll?: boolean;
};

type TAutocompleteContext =
  | ({
      selectedValues: TOption[];
      setSelectedValues: Dispatch<SetStateAction<TOption[]>>;
    } & TAutocomplete)
  | null;

const AutocompleteContext = createContext<TAutocompleteContext>(null);

type TOptionWithInput = [TOption, string];

/** Same grouping/sort as the virtualised list — used for keyboard navigation order. */
function getGroupedSortedOptions(options: TOptionWithInput[]): {
  groupedAndSorted: TOptionWithInput[][];
  sortedFlat: TOptionWithInput[];
} {
  if (options.length === 0) {
    return { groupedAndSorted: [], sortedFlat: [] };
  }

  const packageGroups = Object.values(R.groupBy(options, ([option]) => option.groupKey ?? option.groupValue) || {});

  const sortedGroups = packageGroups
    .map((group) => {
      const representative = group[0][0];

      const sourceTypeOrder: Record<string, number> = {
        ungrouped: -1,
        exchange: 0,
        organisation: 1,
        eod: 2,
      };

      return {
        key: representative.groupKey ?? representative.groupValue,
        label: representative.groupLabel ?? representative.groupValue,
        options: group,
        sourceTypeOrder: sourceTypeOrder[representative.sortSourceType ?? "eod"] ?? 3,
        sourceName: representative.sortSourceName ?? "",
        packageName: representative.sortPackageName ?? representative.groupValue,
      };
    })
    .sort((a, b) => {
      if (a.sourceTypeOrder !== b.sourceTypeOrder) {
        return a.sourceTypeOrder - b.sourceTypeOrder;
      }
      const sourceCompare = a.sourceName.localeCompare(b.sourceName);
      if (sourceCompare !== 0) {
        return sourceCompare;
      }
      return a.packageName.localeCompare(b.packageName);
    });

  const groupedAndSorted = sortedGroups.map((group) => group.options);
  const sortedFlat = groupedAndSorted.flat();

  return { groupedAndSorted, sortedFlat };
}

const LIST_ITEM_HEIGHT = 27;

function highlightParts(text: string, query: string): { text: string; highlight: boolean }[] {
  try {
    return parse(text, match(text, query) ?? []);
  } catch {
    return [{ text, highlight: false }];
  }
}

function HighlightedText({
  parts,
  isDisabled,
  color,
}: {
  parts: { text: string; highlight: boolean }[];
  isDisabled?: boolean;
  color?: string;
}) {
  return (
    <>
      {parts.map((part, index) => (
        <span
          // biome-ignore lint/suspicious/noArrayIndexKey: not sure there is a better key
          key={index}
          className={cn(part.highlight && !isDisabled && "bg-primary/20 text-primary px-0.5 rounded")}
          style={{ color: isDisabled && part.highlight ? "var(--color-neutral-on-disabled)" : color }}
        >
          {part.text}
        </span>
      ))}
    </>
  );
}

type TVirtualizedListProps = {
  anchorEl: HTMLElement | null;
  open: boolean;
  options: TOptionWithInput[];
  onSelect: (option: TOption) => void;
  selectedValues?: TOption[];
  highlightedIndex: number;
  virtuosoRef: RefObject<GroupedVirtuosoHandle | VirtuosoHandle | null>;
};

const VirtualizedList = forwardRef<HTMLDivElement, TVirtualizedListProps>(function VirtualizedList(props, ref) {
  const {
    anchorEl,
    open,
    options,
    onSelect,
    selectedValues: selectedValuesProp,
    highlightedIndex,
    virtuosoRef,
  } = props;
  const {
    listHeight,
    setSelectedValues,
    multiple,
    showAddAll = true,
    selectedValues: selectedValuesContext,
  } = useContext(AutocompleteContext) || {};
  const selectedValues = selectedValuesProp || selectedValuesContext || [];
  const [position, setPosition] = useState<{
    top: number;
    left: number;
    width: number;
    dropdownH: number;
  } | null>(null);

  const inputValue = options?.[0]?.[1] || "";

  // Recalculate position when selectedValues changes (tags added/removed) or when anchorEl changes
  useEffect(() => {
    if (!anchorEl || !open) {
      setPosition(null);
      return;
    }
    const updatePosition = () => {
      const rect = anchorEl.getBoundingClientRect();
      // Same viewport-bounded max-height + flip-up logic as the
      // "Search Curves" dropdown.
      const reservedHeight = 500;
      const available = window.innerHeight - reservedHeight;
      const bounded = Math.min(available, 400);
      const requested = listHeight ?? 400;
      const maxH = available >= 200 ? Math.max(200, bounded) : Math.max(0, bounded);
      const cappedMaxH = Math.min(maxH, requested);
      const spaceBelow = window.innerHeight - rect.bottom - 4;
      const spaceAbove = rect.top - 4;
      // Flip up when the desired height doesn't fit below and there's more
      // room above. This keeps the dropdown from getting awkwardly clipped at
      // the viewport edge.
      const flipUp = spaceBelow < cappedMaxH && spaceAbove > spaceBelow;
      const dropdownH = flipUp ? Math.min(cappedMaxH, spaceAbove) : Math.min(cappedMaxH, spaceBelow);
      const top = flipUp ? Math.max(4, rect.top - dropdownH - 4) : rect.bottom + 4;
      const width = rect.width;
      const left = Math.min(rect.left, Math.max(0, window.innerWidth - width - 8));
      setPosition({ top, left, width, dropdownH });
    };
    // Use requestAnimationFrame to ensure DOM has updated
    requestAnimationFrame(updatePosition);
    // Also update on window resize/scroll
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);
    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [anchorEl, open, listHeight]); // Recalculate when selectedValues length changes

  if (!options || options.length === 0) return null;

  const { groupedAndSorted, sortedFlat: sortedOptions } = getGroupedSortedOptions(options);

  if (!anchorEl || !open || !position) return null;

  const viewportHeight = window.visualViewport?.height ?? window.innerHeight;
  const maxAvailableHeight = viewportHeight - position.top - 8;
  const effectiveListHeight = Math.max(100, Math.min(listHeight || 200, maxAvailableHeight));
  const listStyle = { height: Math.min(effectiveListHeight, position.dropdownH) };
  const hasGroupHeaders = groupedAndSorted.some((group) => {
    const representative = group[0]?.[0];
    return Boolean((representative?.groupLabel ?? representative?.groupValue ?? "").trim());
  });

  const renderOption = (index: number) => {
    const [option] = sortedOptions[index] || [];
    // Virtuoso treats `null` as 0-height, which collapses every following row.
    if (!option) {
      return <div style={{ height: LIST_ITEM_HEIGHT }} />;
    }

    const shortName = option.shortName || option.label.replace(/:\s*[^:]+$/, "");
    const longName = option.longName !== undefined ? option.longName : null;
    const shortNameParts = highlightParts(shortName, inputValue);
    const longNameParts = longName ? highlightParts(longName, inputValue) : [];

    const isDisabled = option.disabled;
    const isSelected = selectedValues?.some((v) => v.value === option.value);
    const isKeyboardHighlight = index === highlightedIndex && !isDisabled;
    const shortNameColor = isDisabled ? "var(--neutral-on-disabled)" : "var(--color-foreground)";
    const longNameColor = isDisabled ? "var(--neutral-on-disabled)" : "var(--color-foreground-muted)";

    const optionText = (
      <span>
        <HighlightedText parts={shortNameParts} isDisabled={isDisabled} color={shortNameColor} />
        {longName && longName !== shortName && (
          <>
            <span style={{ color: longNameColor, marginLeft: "4px" }}>{"("}</span>
            <HighlightedText parts={longNameParts} isDisabled={isDisabled} color={longNameColor} />
            <span style={{ color: longNameColor }}>{")"}</span>
          </>
        )}
      </span>
    );

    const rowClassName = cn(
      "flex items-center w-full min-h-[27px] pl-5 pr-3 py-[2px] text-[length:var(--font-size-base,12px)]",
      isSelected && "pr-8",
    );
    const rowContent = (
      <>
        {optionText}
        {isSelected && (
          <span className="absolute right-3 text-primary" style={{ fontSize: "var(--font-size-base, 12px)" }}>
            ✓
          </span>
        )}
      </>
    );

    return (
      <div
        data-testid="autocomplete-option"
        data-option-value={option.value}
        data-product-name={option.shortName}
        data-source-name={option.sortSourceName}
        data-package-name={option.sortPackageName}
        onClick={() => !isDisabled && onSelect(option)}
        className={cn(
          "transition-colors relative",
          !isDisabled && "cursor-pointer hover:bg-neutral-tonal",
          isKeyboardHighlight && "bg-neutral-tonal",
          isDisabled && "cursor-not-allowed",
        )}
      >
        {isDisabled && option.disabledTooltip ? (
          <TooltipTrigger placement="top" origin="center" block>
            <div className={rowClassName}>{rowContent}</div>
            <Tooltip>{option.disabledTooltip}</Tooltip>
          </TooltipTrigger>
        ) : (
          <div className={rowClassName}>{rowContent}</div>
        )}
      </div>
    );
  };

  return createPortal(
    <div
      ref={ref}
      data-click-away-inside
      style={{
        position: "fixed",
        top: position.top,
        left: position.left,
        width: position.width,
        maxHeight: position.dropdownH,
        zIndex: 99999, // Must be higher than modals
      }}
    >
      <div
        data-testid="autocomplete-dropdown"
        className="bg-card border border-outline rounded-lg shadow-card overflow-hidden autocomplete-dropdown-scrollbar"
        style={{ maxHeight: position.dropdownH }}
      >
        {hasGroupHeaders ? (
          <GroupedVirtuoso
            ref={virtuosoRef as Ref<GroupedVirtuosoHandle>}
            style={listStyle}
            defaultItemHeight={LIST_ITEM_HEIGHT}
            groupCounts={groupedAndSorted.map((group) => group.length)}
            groupContent={(index) => {
              const group = groupedAndSorted[index] || [];
              const representative = group[0][0];
              const label = (representative.groupLabel ?? representative.groupValue ?? "").trim();
              if (!label) {
                // Non-zero so Virtuoso can measure; no blank row above folders (e.g. "None").
                return <div data-testid="autocomplete-empty-group" style={{ height: 1, marginBottom: -1 }} />;
              }

              const isGroupDisabled = group.every(([option]) => option.disabled);
              const parts = highlightParts(label, inputValue);

              return (
                <div
                  className="flex uppercase justify-between items-center px-3 bg-card-muted sticky top-0 z-10"
                  style={{
                    background: "var(--color-card-muted)",
                    fontSize: "var(--font-size-base, 12px)",
                    height: "33px",
                    minHeight: "33px",
                  }}
                >
                  <span className="text-foreground font-medium">
                    <HighlightedText parts={parts} />
                  </span>
                  {multiple && showAddAll && (
                    <button
                      data-testid={`add-all-button-${representative.groupKey ?? representative.groupValue}`}
                      type="button"
                      disabled={isGroupDisabled}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedValues?.((prevValues) => {
                          const newProducts = group
                            .map(([option]) => option)
                            .filter((option) => !prevValues.some((v) => v.value === option.value) && !option.disabled);
                          return [...prevValues, ...newProducts];
                        });
                      }}
                      className="bg-transparent text-[var(--neutral-default)] underline border border-transparent hover:bg-[var(--neutral-tonal)] hover:text-[var(--neutral-on-tonal)] focus:bg-[var(--neutral-tonal)] focus:text-[var(--neutral-on-tonal)] focus:border-[var(--neutral-default)] active:bg-transparent active:text-[var(--neutral-click)] disabled:bg-[var(--neutral-disabled)] disabled:text-[var(--neutral-on-disabled)] px-[15px] h-[27px] text-base rounded-lg cursor-pointer focus:outline-none transition-colors duration-150 flex items-center justify-center"
                      style={{ fontWeight: "var(--font-weights-medium, 500)" }}
                    >
                      Add All
                    </button>
                  )}
                </div>
              );
            }}
            itemContent={renderOption}
          />
        ) : (
          <Virtuoso
            ref={virtuosoRef as Ref<VirtuosoHandle>}
            style={listStyle}
            totalCount={sortedOptions.length}
            defaultItemHeight={LIST_ITEM_HEIGHT}
            itemContent={renderOption}
          />
        )}
      </div>
    </div>,
    document.body,
  );
});

type TTag = {
  values: TOption[];
  onRemove: (option: TOption) => void;
};

function Tags({ values, onRemove }: TTag) {
  const { setSelectedValues, disabled } = useContext(AutocompleteContext) || {};

  const groupedOptions = values.reduce(
    (groups, option) => {
      const key = option.groupKey ?? option.groupValue;
      if (!groups[key]) {
        groups[key] = [];
      }
      groups[key].push(option);
      return groups;
    },
    {} as Record<string, TOption[]>,
  );

  return (
    <>
      {Object.entries(groupedOptions).map(([_, options]) => {
        const onAdd = (option: TOption) => {
          if (disabled) return;
          setSelectedValues?.((prevValues) => {
            return [...prevValues, option];
          });
        };

        return options.map((option) => {
          const shortName = option.shortName || option.label.replace(/:\s*[^:]+$/, "");
          const longName = option.longName !== undefined ? option.longName : null;

          return (
            <div
              key={option.value}
              data-testid="autocomplete-tag"
              className="flex h-[18px] min-w-0 max-w-full items-center overflow-hidden rounded-full border border-outline px-[4px]"
            >
              <span className="min-w-0 truncate text-[10.5px] font-medium text-[var(--neutral-default)]">
                <span className="px-[1px]" style={{ fontWeight: "var(--font-weights-medium, 500)" }}>
                  {shortName}
                </span>
                {longName && longName !== shortName && (
                  <span className="px-[1px]" style={{ fontWeight: "normal" }}>
                    {" "}
                    ({longName})
                  </span>
                )}
              </span>
              {option.sourceLabel && (
                <span
                  className="shrink-0 whitespace-nowrap px-[1px] text-[10.5px] text-[var(--neutral-default)]"
                  style={{ fontWeight: "normal" }}
                >
                  {`: ${option.sourceLabel}`}
                </span>
              )}
              <div className="ml-1 flex shrink-0 gap-1">
                <button
                  type="button"
                  aria-label={`Remove ${shortName}`}
                  disabled={disabled}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (disabled) return;
                    onRemove(option);
                  }}
                  className={cn(
                    "p-0.5 rounded-[4px] border border-transparent transition-colors",
                    disabled
                      ? "cursor-not-allowed"
                      : "cursor-pointer hover:bg-[var(--neutral-tonal)] hover:text-[var(--neutral-on-tonal)]",
                  )}
                >
                  <FaXmark className="text-[var(--neutral-default)] w-[8px] h-[8px]" />
                </button>
                {(option.metadata?.artis_type === "canned" || option.metadata?.artis_type === "sourced") && (
                  <button
                    type="button"
                    disabled={disabled}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (disabled) return;
                      onAdd(option);
                    }}
                    className={cn(
                      "p-0.5 rounded-[4px] border border-transparent transition-colors",
                      disabled
                        ? "cursor-not-allowed"
                        : "cursor-pointer hover:bg-[var(--neutral-tonal)] hover:text-[var(--neutral-on-tonal)]",
                    )}
                  >
                    <FaPlus className="text-[var(--neutral-default)] w-[8px] h-[8px]" />
                  </button>
                )}
              </div>
            </div>
          );
        });
      })}
    </>
  );
}

const escapeRegExp = (string: string) => {
  return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
};

const filterOptions = (options: TOption[], inputValue: string): TOption[] => {
  const searchTerms = inputValue.trim().toLowerCase().split(/\s+/).map(escapeRegExp);

  return options.filter((option) => {
    if (option.hidden) return false;
    const groupText = (option.groupLabel ?? option.groupValue ?? "").toLowerCase();
    const combinedFields = [option.label.toLowerCase(), groupText].join(" ");

    return searchTerms.every((term) => {
      const specialCharRegex = /[^a-z0-9\s]/i;
      let regex: RegExp;

      if (specialCharRegex.test(term)) {
        regex = new RegExp(`(^|\\W)${term}`, "i");
        return regex.test(combinedFields);
      }
      regex = new RegExp(`(^|\\W)${term}`, "i");
      return regex.test(combinedFields);
    });
  });
};

export function Autocomplete(props: TAutocomplete) {
  const {
    multiple,
    options,
    autoComplete = false,
    autoFocus = false,
    singleState,
    multipleState,
    disabled,
    onOpen,
    onSelectValues,
    onSelectValue,
    placeholder,
    disableFilter,
    className,
    loading,
    compact = false,
    onClear,
    clearable = true,
  } = props;

  const [inputValue, setInputValue] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [selectedValue, setSelectedValue] = singleState;
  const [selectedValues, setSelectedValues] = multipleState;

  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const tempStateRef = useRef<TOption | null>(null);

  const filteredOptions = useMemo(
    () => (disableFilter ? options.filter((option) => !option.hidden) : filterOptions(options, inputValue)),
    [disableFilter, options, inputValue],
  );

  const optionsWithInput: TOptionWithInput[] = useMemo(
    () => filteredOptions.map((opt) => [opt, inputValue] as TOptionWithInput),
    [filteredOptions, inputValue],
  );

  const sortedFlat = useMemo(() => getGroupedSortedOptions(optionsWithInput).sortedFlat, [optionsWithInput]);

  const selectableRowIndices = useMemo(
    () => sortedFlat.map((row, i) => (row[0].disabled ? -1 : i)).filter((i): i is number => i >= 0),
    [sortedFlat],
  );

  const virtuosoRef = useRef<GroupedVirtuosoHandle | VirtuosoHandle>(null);
  const [highlightSelectableFlatIdx, setHighlightSelectableFlatIdx] = useState(-1);

  const highlightedRowIndex = (() => {
    if (highlightSelectableFlatIdx < 0 || selectableRowIndices.length === 0) return -1;
    return selectableRowIndices[Math.min(highlightSelectableFlatIdx, selectableRowIndices.length - 1)] ?? -1;
  })();

  /** Closing the menu clears the keyboard highlight. Click-open leaves every row unhighlighted. */
  useEffect(() => {
    if (!isOpen) setHighlightSelectableFlatIdx(-1);
  }, [isOpen]);

  /** When the list is not filtered, typing highlights the matching alphabetical section. */
  useEffect(() => {
    if (!isOpen || !disableFilter) return;
    // Empty query must not paint the first row — same as Dropdown click-open.
    if (!inputValue.trim()) return;
    const labels = sortedFlat.map(([option]) => option.label);
    const rowIndex = findPrefixJumpIndex(labels, inputValue);
    const selectableIdx = selectableRowIndices.indexOf(rowIndex);
    setHighlightSelectableFlatIdx(selectableIdx >= 0 ? selectableIdx : 0);
  }, [isOpen, disableFilter, inputValue, selectableRowIndices, sortedFlat]);

  useEffect(() => {
    if (!isOpen || highlightedRowIndex < 0) return;
    virtuosoRef.current?.scrollToIndex({ index: highlightedRowIndex, align: "start" });
  }, [isOpen, highlightedRowIndex]);

  useEffect(() => {
    if (disabled) setIsOpen(false);
  }, [disabled]);

  const handleOpen = () => {
    if (disabled) return;
    setIsOpen(true);
    onOpen?.();
    if (!multiple && props.value === undefined) {
      setSelectedValue(undefined);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    if (!multiple && props.value === undefined) {
      const tempStateVal = tempStateRef.current;
      if (tempStateVal === undefined || tempStateVal === null) {
        setSelectedValue(props.defaultValue as TOption);
      }
    }
  };

  useEffect(() => {
    if (props.value === undefined) return;
    // Empty string is a real option (e.g. Snapshot/EOD "None"). Skip only when the
    // typed query already matches a non-empty controlled value.
    if (props.value !== "" && props.value === inputValue) return;
    const next = options.find((x) => x.value === props.value);
    // Re-sync when the selection was cleared locally but the controlled value did
    // not change (hitting X while already on "None" keeps value "").
    if (selectedValue?.value === next?.value) return;
    setSelectedValue(next);
  }, [props.value, inputValue, options, setSelectedValue, selectedValue]);

  const handleSelect = (option: TOption) => {
    if (multiple) {
      const isAlreadySelected = selectedValues.some((v) => v.value === option.value);
      if (isAlreadySelected) {
        const newValues = selectedValues.filter((v) => v.value !== option.value);
        setSelectedValues(newValues);
        onSelectValues?.(newValues);
      } else {
        const newValues = [...selectedValues, option];
        setSelectedValues(newValues);
        onSelectValues?.(newValues);
      }
    } else {
      tempStateRef.current = option;
      setSelectedValue(option);
      onSelectValue?.(option);
      setIsOpen(false);
      setInputValue("");
    }
  };

  const handleRemoveTag = (option: TOption) => {
    if (disabled) return;
    const newValues = selectedValues.filter((v) => v.value !== option.value);
    setSelectedValues(newValues);
    onSelectValues?.(newValues);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    setHighlightSelectableFlatIdx(-1);
    if (!isOpen) {
      handleOpen();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      e.preventDefault();
      handleClose();
      return;
    }

    if (e.key === "Tab") {
      if (isOpen) handleClose();
      return;
    }

    if (!isOpen) {
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        e.preventDefault();
        if (selectableRowIndices.length > 0) {
          handleOpen();
          setHighlightSelectableFlatIdx(e.key === "ArrowDown" ? 0 : selectableRowIndices.length - 1);
        }
      }
      return;
    }

    switch (e.key) {
      case "ArrowDown": {
        e.preventDefault();
        setHighlightSelectableFlatIdx((prev) => {
          if (selectableRowIndices.length === 0) return -1;
          if (prev < 0) return 0;
          return Math.min(prev + 1, selectableRowIndices.length - 1);
        });
        break;
      }
      case "ArrowUp": {
        e.preventDefault();
        setHighlightSelectableFlatIdx((prev) => {
          if (selectableRowIndices.length === 0) return -1;
          if (prev < 0) return selectableRowIndices.length - 1;
          return Math.max(prev - 1, 0);
        });
        break;
      }
      case "Enter": {
        if (highlightedRowIndex < 0 || sortedFlat.length === 0 || selectableRowIndices.length === 0) break;
        e.preventDefault();
        const row = sortedFlat[highlightedRowIndex];
        if (row?.[0] && !row[0].disabled) {
          handleSelect(row[0]);
        }
        break;
      }
      default:
        break;
    }
  };

  const handleClear = () => {
    if (disabled) return;
    setInputValue("");
    if (multiple) {
      setSelectedValues([]);
      onSelectValues?.([]);
    } else {
      setSelectedValue(undefined);
      tempStateRef.current = null;
      onClear?.();
    }
    inputRef.current?.focus();
  };

  const displayValue = !multiple && selectedValue && !isOpen ? selectedValue.label : inputValue;
  // Once a pill is selected, an empty search must leave the flex row. A long
  // nowrap pill fills the field, and the empty input would wrap under it as a
  // blank row (WEB-2179). Typing puts the input back so the query stays visible.
  const collapseEmptyInput = multiple && selectedValues.length > 0 && inputValue.length === 0;
  const showClearButton =
    clearable &&
    ((multiple && selectedValues.length > 0) ||
      (!multiple && !!selectedValue && selectedValue.value !== "") ||
      !!inputValue);

  const handleClickAway = () => {
    if (!isOpen) return;
    setIsOpen(false);
    if (!multiple && props.value === undefined) {
      const tempStateVal = tempStateRef.current;
      if (tempStateVal === undefined || tempStateVal === null) {
        setSelectedValue(props.defaultValue as TOption);
      }
    }
  };

  return (
    <AutocompleteContext.Provider value={{ ...props, selectedValues, setSelectedValues }}>
      <ClickAwayListener onClickAway={handleClickAway}>
        <div ref={containerRef} className={cn("relative", className)}>
          <div
            className={cn(
              "flex items-center gap-1 rounded-lg border px-2 py-1 transition-colors overflow-hidden",
              compact ? "min-h-[27px]" : "min-h-[32px]",
              "bg-surface-1 border-outline",
              "hover:bg-surface-2 hover:border-outline-variant",
              "focus-within:border-outline",
              disabled && "bg-neutral-disabled border-neutral-on-disabled cursor-not-allowed opacity-50",
            )}
            onClick={() => {
              if (!disabled) {
                inputRef.current?.focus();
                if (!isOpen) {
                  handleOpen();
                }
              }
            }}
          >
            {autoComplete && (
              <IoSearch
                className={cn("text-neutral shrink-0", disabled && "text-neutral-on-disabled")}
                style={{ fontSize: "1rem" }}
              />
            )}

            <div
              className={cn(
                "relative flex flex-wrap gap-1 items-center flex-1 min-w-0 overflow-y-auto",
                selectedValues?.length ? "max-h-[98px]" : compact ? "h-[19px]" : "h-[25px]",
              )}
            >
              {multiple && selectedValues.length > 0 && <Tags values={selectedValues} onRemove={handleRemoveTag} />}

              <input
                ref={inputRef}
                type="text"
                value={displayValue}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                {...(autoFocus ? { autoFocus: true } : {})}
                disabled={disabled}
                placeholder={selectedValues?.length ? "" : placeholder || "Search..."}
                className={cn(
                  // With no pill yet, keep a minimum so the placeholder stays
                  // legible (WEB-1888). After a pill is selected the empty input
                  // is pulled out of the row; a minimum width would wrap under a
                  // full-width pill and leave a blank line.
                  "bg-transparent outline-none text-sm",
                  collapseEmptyInput ? "absolute h-0 w-0 overflow-hidden p-0" : "min-w-[60px] flex-1",
                  "text-foreground placeholder:text-foreground-muted",
                  disabled && "text-neutral-on-disabled placeholder:text-neutral-on-disabled cursor-not-allowed",
                )}
              />
            </div>
            {loading && (
              <div className="w-4 h-4 border-2 border-neutral border-t-transparent rounded-full animate-spin" />
            )}
            {showClearButton && !loading && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleClear();
                }}
                disabled={disabled}
                className={cn("p-0.5", disabled && "cursor-not-allowed")}
              >
                <CloseIcon className={cn("opacity-70", disabled && "opacity-50")} disabled={disabled} />
              </button>
            )}
            <button
              type="button"
              tabIndex={-1}
              onClick={(e) => {
                e.stopPropagation();
                if (isOpen) {
                  handleClose();
                } else {
                  handleOpen();
                  inputRef.current?.focus();
                }
              }}
              disabled={disabled}
              className={cn("p-1 transition-transform", isOpen && "rotate-180", disabled && "cursor-not-allowed")}
            >
              <FaChevronDown
                className={cn("text-neutral", disabled && "text-neutral-on-disabled")}
                style={{ fontSize: "0.75rem" }}
              />
            </button>
          </div>

          <VirtualizedList
            ref={listRef}
            anchorEl={containerRef.current}
            open={isOpen}
            options={optionsWithInput}
            onSelect={handleSelect}
            selectedValues={multiple ? selectedValues : selectedValue ? [selectedValue] : []}
            highlightedIndex={highlightedRowIndex}
            virtuosoRef={virtuosoRef}
          />
        </div>
      </ClickAwayListener>
    </AutocompleteContext.Provider>
  );
}
